import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Switch } from "@shared/ui/switch";
import { Button } from "@shared/ui/button";
import { Clock } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useUpdateAvailability } from "@features/wx/fieldForce/hooks";
import { logEvent } from "@shared/analytics/analytics";

type PersonData = {
  id: string;
  name: string;
  role: string;
  zone: string;
  area: string;
  contact: string;
  joiningDate: string;
  status: string;
  email?: string;
  imei?: string;
  address?: string;
  additionalData?: {
    availabilitySchedule?: Array<{
      day: string;
      startTime: string | null;
      endTime: string | null;
      isOvertime: boolean;
      isWeeklyOff: boolean;
    }>;
  };
};

type DaySchedule = {
  available: boolean;
  startTime: string;
  endTime: string;
  availableOvertime: boolean;
};

type WeeklySchedule = {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
};

type AvailabilityFormData = {
  weeklySchedule: WeeklySchedule;
};

interface EditAvailabilityTabProps {
  initialPersonData?: PersonData | null;
  // ADD: New props for state management
  currentFormData?: AvailabilityFormData | null;
  onFormDataChange?: (data: AvailabilityFormData) => void;
}

const defaultSchedule: DaySchedule = {
  available: true,
  startTime: "09:00",
  endTime: "17:00",
  availableOvertime: false,
};

// Helper function to convert day name to lowercase for object key
const getDayKey = (dayName: string): keyof WeeklySchedule => {
  return dayName.toLowerCase() as keyof WeeklySchedule;
};

// Helper function to format time from API (if needed)
const formatTime = (time: string | null): string => {
  if (!time) return "09:00";

  // If time is already in HH:MM format, return as is
  if (time.match(/^\d{2}:\d{2}$/)) {
    return time;
  }

  // Handle other time formats if needed
  // For example, if API returns "9:00" instead of "09:00"
  if (time.match(/^\d{1}:\d{2}$/)) {
    return `0${time}`;
  }

  // Default fallback
  return "09:00";
};

export const EditAvailabilityTab = forwardRef<any, EditAvailabilityTabProps>(
  ({ initialPersonData, currentFormData, onFormDataChange }, ref) => {
    const navigate = useNavigate();
    const remoteUtilityId = useRemoteUtilityId();
    const [dayValidationErrors, setDayValidationErrors] = useState<
      Record<string, string>
    >({});
    const { ff_no } = useParams<{ ff_no: string }>();
    const updateAvailability = useUpdateAvailability(ff_no);

    // UPDATED: Use currentFormData if available, otherwise use defaults
    const getInitialWeeklySchedule = (): WeeklySchedule => {
      if (currentFormData) return currentFormData.weeklySchedule;

      return {
        monday: { ...defaultSchedule },
        tuesday: { ...defaultSchedule },
        wednesday: { ...defaultSchedule },
        thursday: { ...defaultSchedule },
        friday: { ...defaultSchedule },
        saturday: { ...defaultSchedule, available: false },
        sunday: { ...defaultSchedule, available: false },
      };
    };

    const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule>(
      getInitialWeeklySchedule
    );

    useEffect(() => {
      // UPDATED: Only populate from initialPersonData if no currentFormData exists
      if (
        initialPersonData &&
        initialPersonData.additionalData?.availabilitySchedule &&
        !currentFormData
      ) {
        const scheduleData =
          initialPersonData.additionalData.availabilitySchedule;

        // Create new schedule object from API data
        const newSchedule: WeeklySchedule = {
          monday: { ...defaultSchedule },
          tuesday: { ...defaultSchedule },
          wednesday: { ...defaultSchedule },
          thursday: { ...defaultSchedule },
          friday: { ...defaultSchedule },
          saturday: { ...defaultSchedule, available: false },
          sunday: { ...defaultSchedule, available: false },
        };

        // Map API data to form structure
        scheduleData.forEach((dayData) => {
          const dayKey = getDayKey(dayData.day);
          if (dayKey && newSchedule[dayKey]) {
            // FIX: isWeeklyOff = true means NOT available, so we need to invert it
            const isAvailable = !dayData.isWeeklyOff;

            newSchedule[dayKey] = {
              available: isAvailable,
              startTime:
                isAvailable && dayData.startTime
                  ? formatTime(dayData.startTime)
                  : defaultSchedule.startTime, // Use default time instead of null
              endTime:
                isAvailable && dayData.endTime
                  ? formatTime(dayData.endTime)
                  : defaultSchedule.endTime, // Use default time instead of null
              availableOvertime: dayData.isOvertime || false,
            };
          }
        });

        setWeeklySchedule(newSchedule);
      }
    }, [initialPersonData, currentFormData]);

    // Enhanced validation function that returns errors per day
    const validateSchedule = () => {
      const errors: Record<string, string> = {};

      // Check if at least one day is available
      const hasAtLeastOneWorkingDay = Object.values(weeklySchedule).some(
        (schedule) => schedule.available
      );

      if (!hasAtLeastOneWorkingDay) {
        // For general errors, you could use a toast or keep a general error display
        return { general: "At least one working day must be selected" };
      }

      // Validate time ranges for available days
      for (const [day, schedule] of Object.entries(weeklySchedule)) {
        if (schedule.available) {
          if (!schedule.startTime || !schedule.endTime) {
            errors[day] = "Start time and end time are required";
            continue;
          }

          const startTime = new Date(`2000-01-01T${schedule.startTime}`);
          const endTime = new Date(`2000-01-01T${schedule.endTime}`);

          if (startTime >= endTime) {
            errors[day] = "End time must be after start time";
          }
        }
      }

      return errors;
    };

    useImperativeHandle(ref, () => ({
      getData: () => ({
        weeklySchedule,
        availabilitySchedule: Object.entries(weeklySchedule).map(
          ([day, schedule]) => ({
            day: day.charAt(0).toUpperCase() + day.slice(1),
            startTime: schedule.available ? schedule.startTime : null,
            endTime: schedule.available ? schedule.endTime : null,
            isOvertime: schedule.availableOvertime,
            isWeeklyOff: !schedule.available,
          })
        ),
      }),
      validate: () => {
        const errors = validateSchedule();

        if (Object.keys(errors).length > 0) {
          setDayValidationErrors(errors);
          return false;
        }

        return true;
      },
    }));

    const handleScheduleChange = (
      day: keyof WeeklySchedule,
      field: keyof DaySchedule,
      value: string | boolean
    ) => {
      const newWeeklySchedule = {
        ...weeklySchedule,
        [day]: {
          ...weeklySchedule[day],
          [field]: value,
        },
      };

      setWeeklySchedule(newWeeklySchedule);

      // UPDATED: Notify parent about form data changes
      onFormDataChange?.({
        weeklySchedule: newWeeklySchedule,
      });

      // Clear validation error for this day when user makes changes
      if (dayValidationErrors[day]) {
        setDayValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[day];
          return newErrors;
        });
      }
    };

    const handleAvailabilitySubmit = () => {
      // Clear previous errors
      setDayValidationErrors({});

      // Validate the schedule
      const errors = validateSchedule();

      // If there are errors, show them and return
      if (Object.keys(errors).length > 0) {
        setDayValidationErrors(errors);
        return;
      }

      // Create availability data
      const availabilityData = {
        weeklySchedule,
        availabilitySchedule: Object.entries(weeklySchedule).map(
          ([day, schedule]) => ({
            day: day.charAt(0).toUpperCase() + day.slice(1),
            startTime: schedule.available ? schedule.startTime : null,
            endTime: schedule.available ? schedule.endTime : null,
            isOvertime: schedule.availableOvertime,
            isWeeklyOff: !schedule.available,
          })
        ),
      };

      // Create payload to match the required structure
      const payload = {
        ff_no: ff_no,
        remote_utility_id: remoteUtilityId,
        is_field_force_info: true,
        additional_data: {
          availability_schedule: availabilityData.availabilitySchedule.map(
            (daySchedule: any) => ({
              day: daySchedule.day,
              start_time: daySchedule.startTime,
              end_time: daySchedule.endTime,
              is_overtime: daySchedule.isOvertime,
              is_weekly_off: daySchedule.isWeeklyOff,
            })
          ),
        },
      };

      updateAvailability.mutateAsync(payload).then(() => {
        // UPDATED: Clear saved form data on successful submit
        logEvent(`Field Force ${ff_no} Availability Updated`, {
          availability_schedule: availabilityData.availabilitySchedule.map(
            (daySchedule: any) => ({
              day: daySchedule.day,
              start_time: daySchedule.startTime,
              end_time: daySchedule.endTime,
              is_overtime: daySchedule.isOvertime,
              is_weekly_off: daySchedule.isWeeklyOff,
            })
          ),
        })
        onFormDataChange?.(null);
        navigate(`/service-orders/field-force/`, { replace: true });
      });
    };

    const handleReset = () => {
      // UPDATED: Clear saved form data on reset
      onFormDataChange?.(null);
      navigate(`/service-orders/field-force/`);
    };

    const EditDayScheduleCard = ({
      day,
      displayName,
    }: {
      day: keyof WeeklySchedule;
      displayName: string;
    }) => (
      <div className="border rounded-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">{displayName} Schedule</h4>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Available</span>
            <Switch
              checked={weeklySchedule[day].available}
              onCheckedChange={(checked) =>
                handleScheduleChange(day, "available", checked)
              }
            />
          </div>
        </div>

        {weeklySchedule[day].available ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`${day}-start`}>Start Time</Label>
                <Input
                  id={`${day}-start`}
                  type="time"
                  placeholder="Select Start Time"
                  value={weeklySchedule[day].startTime}
                  onChange={(e) =>
                    handleScheduleChange(day, "startTime", e.target.value)
                  }
                  className={dayValidationErrors[day] ? "border-red-500" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`${day}-end`}>End Time</Label>
                <Input
                  id={`${day}-end`}
                  type="time"
                  placeholder="Select End Time"
                  value={weeklySchedule[day].endTime}
                  onChange={(e) =>
                    handleScheduleChange(day, "endTime", e.target.value)
                  }
                  className={dayValidationErrors[day] ? "border-red-500" : ""}
                />
              </div>
            </div>

            {/* Show validation error below the time inputs */}
            {dayValidationErrors[day] && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mt-2">
                <p className="text-red-700 text-sm flex items-center gap-2">
                  <span className="text-red-500">⚠️</span>
                  {dayValidationErrors[day]}
                </p>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Switch
                id={`${day}-overtime`}
                checked={weeklySchedule[day].availableOvertime}
                onCheckedChange={(checked) =>
                  handleScheduleChange(day, "availableOvertime", checked)
                }
              />
              <Label htmlFor={`${day}-overtime`}>Overtime Availability</Label>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Toggle availability to edit schedule
          </p>
        )}
      </div>
    );

    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Clock size={18} />
          Weekly Schedule
        </h3>

        <div className="space-y-4">
          <EditDayScheduleCard day="monday" displayName="Monday" />
          <EditDayScheduleCard day="tuesday" displayName="Tuesday" />
          <EditDayScheduleCard day="wednesday" displayName="Wednesday" />
          <EditDayScheduleCard day="thursday" displayName="Thursday" />
          <EditDayScheduleCard day="friday" displayName="Friday" />
          <EditDayScheduleCard day="saturday" displayName="Saturday" />
          <EditDayScheduleCard day="sunday" displayName="Sunday" />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleReset}>
            Cancel
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleAvailabilitySubmit}
          >
            Update Technician
          </Button>
        </div>
      </div>
    );
  }
);
